// The canned chat message the "Resolve with {agent}" CTA sends into the Maestro chat, shared by the
// task-details side panel (use-task-detail-modal) and the full task page (pages/tasks/[id]).
//
// It is not a pleasantry — it is the whole instruction the model receives. The bare form ("Let's resolve
// the task …") once reached a task that was ACTION_NEEDED over two missing phone numbers, and the model
// read "resolve" as "close": it set the task COMPLETED and wrote a completion log claiming every send
// landed, over a blocker written in the very summary it could have read. So on an ACTION_NEEDED task the
// message now carries the blocker itself and what "resolve" means: give the task what it is waiting on,
// and only close it once that is actually done. Mirrored 1:1 on iOS (`resolveTaskMessage` in
// AppRouter.swift) and Android (`resolveTaskMessage` in ChatContext.kt) — same wording, same keys per
// surface (`tasks.detail.resolveMessage[Blocked]` here, `chat.resolveTask[Blocked]Message` there).
// The harness enforces the same rule structurally: ManageTask refuses ACTION_NEEDED → COMPLETED
// without a `resolution` naming how the blocker was met.

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface TaskResolveShape {
  /** The already-resolved display title (callers apply their own untitled fallback first). */
  title: string;
  /** The task's raw status string, e.g. 'ACTION_NEEDED'. */
  status?: string;
  /** The card's `display.summary` — on an ACTION_NEEDED task, the ask itself. */
  summary?: string;
}

export function taskResolveMessage(t: Translate, task: TaskResolveShape): string {
  const blocker = task.summary?.trim();
  if (task.status === 'ACTION_NEEDED' && blocker) {
    // The summary is user/agent text: interpolation must not HTML-escape it on its way into a chat box.
    return t('tasks.detail.resolveMessageBlocked', {
      title: task.title,
      summary: blocker,
      interpolation: { escapeValue: false },
    });
  }
  return t('tasks.detail.resolveMessage', { title: task.title, interpolation: { escapeValue: false } });
}

/**
 * The canned message the "Discuss with {agent}" / "Follow up with {agent}" CTAs send.
 *
 * Deliberately NOT the resolve wording. Those two actions are offered on a task that is the agent's turn
 * or already finished (see `lib/task-actions.ts`), where "resolve" is either premature or meaningless —
 * and asking a model to resolve a task nobody is blocked on invites exactly the close-it-anyway reading
 * the blocked variant above exists to prevent.
 */
export function taskDiscussMessage(t: Translate, title: string): string {
  return t('tasks.detail.discussMessage', { title, interpolation: { escapeValue: false } });
}
